import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/axiosInstance";
import {
  Box, Button, TextField, Paper, Typography, CircularProgress, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Lock as LockIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import "./EditUser.css";

const PermissionSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    className={`permission-switch ${checked ? "is-on" : "is-off"}`}
    role="switch"
    aria-checked={checked}
    aria-label="Toggle permission"
    disabled={disabled}
    onClick={() => onChange(!checked)}
  >
    <span className="permission-switch-track">
      <span className="permission-switch-thumb" />
    </span>
  </button>
);

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState({
    id: "", username: "", role: "", active: true,
    canCreateCustomer: false, canManageChallan: false, canManageCustomer: false,
    canManageGoodsItem: false, canManagePassbook: false, canManageProfile: false,
    canManageSetting: false, canAccessCRM: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const getUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/users/getById/${id}`);
      const data = response.data;
      // Backend might return ROLE_ADMIN -> normalize
      const normalizedRole = data.role?.replace('ROLE_', '').toUpperCase() || "";

      setUser({
        id: data.id || "",
        username: data.username || "",
        role: normalizedRole,
        active: data.active?? true,
        canCreateCustomer:!!data.canCreateCustomer,
        canManageChallan:!!data.canManageChallan,
        canManageCustomer:!!data.canManageCustomer,
        canManageGoodsItem:!!data.canManageGoodsItem,
        canManagePassbook:!!data.canManagePassbook,
        canManageProfile:!!data.canManageProfile,
        canManageSetting:!!data.canManageSetting,
        canAccessCRM:!!data.canAccessCRM,
      });
      setError("");
    } catch (err) {
      console.error("Error fetching user:", err);
      setError(err.response?.status === 401? "Authentication failed. Please login again." : "Failed to load user data.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { if (id) getUser(); }, [id, getUser]);

  // FIXED: handles both TextField and Switch correctly
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    const isCheckbox = e.target.type === 'checkbox';
    setUser(prev => ({
     ...prev,
      [name]: isCheckbox? checked : value
    }));
    setError("");
  };

  // Permission switch handler
  // MUI Switch provides the new boolean value as the second onChange argument.
  // Using that value directly avoids relying on the event target and guarantees
  // every permission switch can be turned ON and OFF independently.
  const handlePermissionChange = (permission, checked) => {
    setUser(prev => ({
      ...prev,
      [permission]: Boolean(checked),
    }));
    setError("");
  };

  const updateUser = async (e) => {
    e.preventDefault();
    if (!user.username.trim()) { setError("Username is required"); return; }
    if (!user.role) { setError("Role is required"); return; }

    try {
      setLoading(true);
      const minimalData = {
        username: user.username,
        role: user.role,
        active: user.active,
        canCreateCustomer: user.canCreateCustomer,
        canManageChallan: user.canManageChallan,
        canManageCustomer: user.canManageCustomer,
        canManageGoodsItem: user.canManageGoodsItem,
        canManagePassbook: user.canManagePassbook,
        canManageProfile: user.canManageProfile,
        canManageSetting: user.canManageSetting,
        canAccessCRM: user.canAccessCRM
      };

      // FIXED: Use api instance, not fetch. It auto-adds token
      const response = await api.put(`/settings/users/update/${id}`, minimalData);
      console.log("Update success:", response.data);
      toast.success("User updated successfully!");
      navigate("/dashboard/users");
    } catch (err) {
      console.error("Error updating:", err.response?.data || err);
      const msg = err.response?.data?.message || err.message;
      if (err.response?.status === 401) setError("Authentication failed. Please login again.");
      else setError(msg);
      toast.error(`Failed to update: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading &&!user.username) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: '400px', alignItems: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 3 }, maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ maxWidth: 1400, width: '100%', mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/dashboard/users")} sx={{ mb: 1.5, color: '#64748b', fontWeight: 500 }}>Back to Users</Button>

        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontWeight: 700, fontSize: 15 }}>
              {user.username ? user.username.charAt(0).toUpperCase() : '?'}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>Edit User</Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }} noWrap>
                {user.username || 'User'} {user.role ? `· ${user.role}` : ''}
              </Typography>
            </Box>
          </Box>

          <Box component="form" onSubmit={updateUser} sx={{ p: { xs: 2, sm: 3 } }}>
            {error && <Box sx={{ bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#dc2626', p: '0.75rem 1rem', borderRadius: '8px', mb: 2, fontSize: '0.875rem' }}>{error}</Box>}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, gap: { xs: 2.5, md: 4 } }}>
              {/* Left: account details */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>Account Details</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 0.75 }}>Username *</Typography>
                    <TextField fullWidth name="username" value={user.username} onChange={handleInputChange} disabled={loading} required size="medium" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', mb: 0.75 }}>Role *</Typography>
                    <TextField
                      fullWidth
                      select
                      name="role"
                      value={user.role || ""}
                      onChange={handleInputChange}
                      disabled={loading}
                      required
                      size="medium"
                    >
                      <MenuItem value="">Select Role</MenuItem>
                      <MenuItem value="ADMIN">Admin</MenuItem>
                      <MenuItem value="USER">User</MenuItem>
                      <MenuItem value="EMPLOYEE">Employee</MenuItem>
                    </TextField>
                  </Box>
                </Box>
              </Box>

              {/* Right: permissions */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>Permissions</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
                  {[
                    ['canManageCustomer', 'Can Manage Customer'],
                    ['canCreateCustomer', 'Can Create Customer'],
                    ['canManageGoodsItem', 'Can Manage Goods'],
                    ['canManageChallan', 'Can Manage Challan'],
                    ['canManageProfile', 'Can Manage Profile'],
                    ['canManageSetting', 'Can Manage Settings'],
                    ['canManagePassbook', 'Can Manage Passbook'],
                    ['canAccessCRM', 'Can Access CRM'],
                  ].map(([key, label]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, border: '1px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', position: 'relative', zIndex: 1 }}>
                      <Typography sx={{ color: '#475569', fontWeight: 500, fontSize: '14px', pointerEvents: 'none' }}>{label}</Typography>
                      <PermissionSwitch
                        checked={!!user[key]}
                        onChange={(checked) => handlePermissionChange(key, checked)}
                        disabled={loading}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3, pt: 2.5, borderTop: '1px solid #e2e8f0' }}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ minWidth: 150, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', fontWeight: 600 }}>{loading? 'Updating...' : 'Update User'}</Button>
              <Button type="button" variant="outlined" onClick={() => navigate("/dashboard/users")} disabled={loading} sx={{ minWidth: 110 }}>Cancel</Button>
              <Button
                type="button"
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setChangePasswordDialog(true)}
                disabled={loading}
                sx={{
                  ml: { sm: 'auto' },
                  borderColor: '#f59e0b',
                  color: '#d97706',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#d97706',
                    background: '#fffbeb'
                  }
                }}
              >
                Change Password
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Dialog open={changePasswordDialog} onClose={() => !changingPassword && setChangePasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setChangePasswordDialog(false);
            setPasswordData({ newPassword: "", confirmPassword: "" });
          }} disabled={changingPassword}>Cancel</Button>
          <Button
            variant="contained"
            disabled={changingPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword}
            onClick={async () => {
              if (passwordData.newPassword !== passwordData.confirmPassword) {
                toast.error("Passwords do not match");
                return;
              }
              try {
                setChangingPassword(true);
                await api.put(`/settings/users/${user.username}/change-password`, {
                  newPassword: passwordData.newPassword
                });
                toast.success("Password changed successfully");
                setChangePasswordDialog(false);
                setPasswordData({ newPassword: "", confirmPassword: "" });
              } catch (err) {
                toast.error(err.response?.data?.message || "Failed to change password");
              } finally {
                setChangingPassword(false);
              }
            }}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important',
              color: '#fff !important',
              '& .MuiButton-label, & .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#fff !important' },
              '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%) !important' },
              '&.Mui-disabled': { bgcolor: '#cbd5e1 !important', color: '#1e293b !important', opacity: 1 },
              '&.Mui-disabled .MuiButton-label': { color: '#1e293b !important' }
            }}
          >
            <span style={{ color: 'inherit' }}>{changingPassword ? "Changing..." : "Change Password"}</span>
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditUser;